import React, { useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import ContentEntryDataGetter from 'layers/content/Hocs/ContentEntryDataGetter'
import withApiError from 'layers/errorHandling/apiError/component'
import {
  ERROR_TYPE_CONTENTFUL,
  SHOW_CONTENT_ON_401,
} from 'layers/errorHandling/apiError'
import {
  operations as siteOperations,
  selectors as siteSelectors,
  actions as siteActions,
} from 'store/siteManager'
import { Formik } from 'formik'
import { validationSchema, validationSchemas, fieldNameSets } from './data'
import { additionalGoalFields } from 'components/GoalsModal/flag.off/data'

import SummaryModalComponent from './component'

export const SummaryModalContainer = ({
  clearError,
  flowLabel,
  hasApiError,
  handleClose,
  modals,
  siteId,
  preferences,
  updateSitePreferences,
}) => {
  const goal3HasValues =
    !!preferences?.goal3Text && preferences.goal3TextIsCompleted
  const goal4HasValues =
    !!preferences?.goal4Text && preferences.goal4TextIsCompleted
  const goal5HasValues =
    !!preferences?.goal5Text && preferences.goal5TextIsCompleted

  const showGoal3 = goal3HasValues || goal4HasValues || goal5HasValues
  const showGoal4 = goal4HasValues || goal5HasValues
  const showGoal5 = goal5HasValues

  const [currentStep, setCurrentStep] = useState(0)
  const [additionalGoalCount, setAdditionalGoalCount] = useState(
    showGoal3 + showGoal4 + showGoal5, // boolean javascript magic
  )

  const handleChangeGoalCount = (delta, index, setFieldValue, values) => {
    const newGoalCount = additionalGoalCount + delta
    const isDecrement = delta === -1

    if (isDecrement && newGoalCount >= 0) {
      // If removing a goal at a specific index, shift values up from subsequent goals
      if (index !== undefined && index < additionalGoalCount - 1) {
        // Shift values up from subsequent goals
        for (let i = index; i < additionalGoalCount - 1; i++) {
          const currentGoal = additionalGoalFields[i]
          const nextGoal = additionalGoalFields[i + 1]

          // Move text and date from next goal to current goal
          setFieldValue(currentGoal.text, values[nextGoal.text])
          setFieldValue(currentGoal.date, values[nextGoal.date])
        }

        // Clear the last goal's values
        const lastGoal = additionalGoalFields[additionalGoalCount - 1]
        setFieldValue(lastGoal.text, null)
        setFieldValue(lastGoal.date, null)
        setFieldValue(lastGoal.show, false)
      } else {
        // If no index specified or removing last goal, just clear that goal's values
        const adjustedGoalFields = additionalGoalFields[newGoalCount]
        setFieldValue(adjustedGoalFields.date, null)
        setFieldValue(adjustedGoalFields.text, null)
        setFieldValue(adjustedGoalFields.show, false)
      }
    } else if (newGoalCount - 1 >= 0) {
      setFieldValue(additionalGoalFields[newGoalCount - 1].show, true)
      setFieldValue(additionalGoalFields[newGoalCount - 1].text, null)
      setFieldValue(additionalGoalFields[newGoalCount - 1].date, null)
    }

    setAdditionalGoalCount(newGoalCount)
  }

  const handleStepChange = toStep => setCurrentStep(toStep)

  const handleSave = async values => {
    let fieldsToSave = fieldNameSets[currentStep]
    const newSitePreferences = {
      k5Digital: {},
      mspDigital: {},
    }

    fieldsToSave.forEach(field => {
      if (values[field]) {
        newSitePreferences.k5Digital[field] = values[field]
        newSitePreferences.mspDigital[field] = values[field]
      }
      if (field.includes('IsCompleted')) {
        newSitePreferences.k5Digital[field] = true
        newSitePreferences.mspDigital[field] = true
      }
    })

    // have to handle the programIsEnabled separately
    if (currentStep === 1) {
      newSitePreferences.k5Digital.programIsEnabled =
        values.programIsEnabled.k5Digital
      newSitePreferences.mspDigital.programIsEnabled =
        values.programIsEnabled.mspDigital
    }
    // have to handle the additional goals separately
    if (currentStep === 3) {
      for (let i = 0; i < 3; i++) {
        const isGoalEnabled = additionalGoalCount > i
        const goalDateField = `goal${i + 3}Date`
        const goalTextField = `goal${i + 3}Text`
        const goalDateIsCompletedField = `${goalDateField}IsCompleted`
        const goalTextIsCompletedField = `${goalTextField}IsCompleted`
        newSitePreferences.k5Digital[goalDateIsCompletedField] = isGoalEnabled
        newSitePreferences.k5Digital[goalTextIsCompletedField] = isGoalEnabled
        newSitePreferences.mspDigital[goalDateIsCompletedField] = isGoalEnabled
        newSitePreferences.mspDigital[goalTextIsCompletedField] = isGoalEnabled
      }
    }

    await updateSitePreferences(siteId, newSitePreferences)
  }

  const closeModal = () => {
    clearError('sitePreferences')
    handleClose()
  }

  const initialValues = {
    ...preferences,
    programStartDate: new Date(preferences?.programStartDate) || null,
    classCreationGoalDate: preferences?.classCreationGoalDate
      ? new Date(preferences.classCreationGoalDate)
      : null,
    lessonCompletionGoalDate: preferences?.lessonCompletionGoalDate
      ? new Date(preferences.lessonCompletionGoalDate)
      : null,
    showGoal3,
    showGoal4,
    showGoal5,
    goal3Date: preferences?.goal3Date ? new Date(preferences.goal3Date) : null,
    goal4Date: preferences?.goal4Date ? new Date(preferences.goal4Date) : null,
    goal5Date: preferences?.goal5Date ? new Date(preferences.goal5Date) : null,
  }

  return (
    <Formik
      initialValues={initialValues}
      validateOnMount
      validationSchema={validationSchema}
    >
      {({
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched,
        setSubmitting,
        isSubmitting,
        isValid,
        resetForm,
        setTouched,
      }) => (
        <SummaryModalComponent
          additionalGoalCount={additionalGoalCount}
          currentStep={currentStep}
          errors={errors}
          flowLabel={flowLabel}
          goBack={() => {
            handleStepChange(0)
            resetForm()
          }}
          handleChange={(name, value) => {
            setFieldTouched(name)
            setFieldValue(name, value)
          }}
          handleChangeGoalCount={(delta, index) =>
            handleChangeGoalCount(delta, index, setFieldValue, values)
          }
          handleClose={closeModal}
          handleNextButtonClick={async () => {
            if (currentStep === 0) {
              closeModal()
              return
            }
            const validationSchema = validationSchemas[currentStep]
            const isValid = await validationSchema.isValid(values)
            if (!isValid) {
              const fieldNames = fieldNameSets[currentStep]
              const touchedFields = fieldNames.reduce((acc, name) => {
                acc[name] = true
                return acc
              }, {})
              await setTouched(touchedFields)
              return
            }
            setSubmitting(true)
            await handleSave(values)
            setSubmitting(false)
            closeModal()
          }}
          handleStepChange={handleStepChange}
          hasApiError={hasApiError}
          isSubmitting={isSubmitting}
          isValid={isValid}
          modals={modals}
          preferences={preferences}
          touched={touched}
          values={values}
        />
      )}
    </Formik>
  )
}

const mapStateToProps = (state, ownProps) => {
  const site = siteSelectors?.selectSiteById(state, ownProps.siteId)
  const { sitePreferences = {} } = site
  const { k5Digital = {}, mspDigital = {} } = sitePreferences

  // spread copy values so that formik values have same names
  const { ...preferences } = k5Digital
  const { sitePreferences: sitePreferencesAPIResults } = state.siteManager
  const { error } = sitePreferencesAPIResults

  preferences.programIsEnabled = {
    k5Digital: k5Digital?.programIsEnabled || false,
    mspDigital: mspDigital?.programIsEnabled || false,
  }

  return {
    hasApiError: !!error,
    preferences,
  }
}

const mapDispatchToProps = {
  updateSitePreferences: siteOperations.updateSitePreferences,
  clearError: siteActions.clearError,
}

SummaryModalContainer.propTypes = {
  clearError: PropTypes.func,
  dispatch: PropTypes.func,
  flowLabel: PropTypes.string,
  handleClose: PropTypes.func,
  hasApiError: PropTypes.bool,
  modals: PropTypes.array,
  preferences: PropTypes.object,
  siteId: PropTypes.number,
  sitePreferences: PropTypes.object,
  updateSitePreferences: PropTypes.func,
}

const mapper = entry => {
  const { modalFlows } = entry || {}
  if (!modalFlows) return {}
  const modals = [
    modalFlows?.[4]?.modals?.[0] || {},
    modalFlows?.[0]?.modals?.[0] || {},
    modalFlows?.[1]?.modals?.[1] || {},
    modalFlows?.[2]?.modals?.[1] || {},
    modalFlows?.[3]?.modals?.[0] || {},
  ]

  modals[0].flowLabel = modalFlows?.[4]?.flowLabel
  modals[1].flowLabel = modalFlows?.[0]?.flowLabel
  modals[2].flowLabel = modalFlows?.[1]?.flowLabel
  modals[3].flowLabel = modalFlows?.[2]?.flowLabel
  modals[4].flowLabel = modalFlows?.[3]?.flowLabel

  return {
    modals,
  }
}

const options = {
  entryId: '7o0HkKLT0Cy171nqL7l4Ib',
  include: 3,
  mapper,
  spread: true,
}

export default ContentEntryDataGetter(
  withApiError(
    SummaryModalContainer,
    [ERROR_TYPE_CONTENTFUL],
    [SHOW_CONTENT_ON_401],
  ),
  options,
  connect(mapStateToProps, mapDispatchToProps),
)
